import { inject, Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";

import { BehaviorSubject, merge, MonoTypeOperatorFunction, Observable, of } from "rxjs";
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from "rxjs/operators";
import { CANTONS_NODEID, COMMUNES_NODEID } from "src/consts";
import {
  ExtendedHierarchyNode,
  HierarchyNodeViewModel,
  HierarchyService,
  LogicalPage,
  NodeId,
  NodeVM,
  OrganizationId,
  PublicationNode,
} from "src/core/backend/controllers";
import { cacheClear$ } from "src/modules/shared/services/cache-clear.service";
import { HierarchyCacheService } from "src/modules/shared/services/hierarchy-cache.service";
import { fromRouter, NodeIdAndPage } from "src/shared/store/selectors";
import { distinctUntilChangedJson, isNotNull, isTruthy } from "src/shared/utils";
import { Cacheable } from "ts-cacheable";

interface ContentNode {
  content?: string | null;
  titles?: string[];
}

export interface PathInfo {
  path: NodeId[];
  document?: HierarchyNodeViewModel;
  chapter?: HierarchyNodeViewModel;
  organization?: HierarchyNodeViewModel;
  publication?: PublicationNode;
  contentNode: ContentNode | undefined;
  page?: LogicalPage;
  nodeId?: NodeId;
}

@Injectable({ providedIn: "root" })
export class LibraryNavigationService {
  private readonly store = inject(Store);
  private readonly nodeService = inject(HierarchyService);
  private readonly cacheService = inject(HierarchyCacheService);

  //
  readonly docNodePage$ = this.store.pipe(select(fromRouter.selectDocNodePage));

  readonly searchQuery$ = this.store.pipe(select(fromRouter.selectSearchQuery));

  private readonly _forcedNodeId$ = new BehaviorSubject<NodeId | undefined>(undefined);

  private readonly nodeIdAndPage$ = merge(
    this._forcedNodeId$.pipe(
      filter(isNotNull),
      map((nodeId): NodeIdAndPage => ({ nodeId, pageNumber: undefined }))
    ),
    this.store.pipe(select(fromRouter.selectDocNodePage))
  ).pipe(distinctUntilChangedJson());

  readonly pathInfo$ = this.nodeIdAndPage$.pipe(
    switchMap((nodeIdAndPage) => this.getPathInfo(nodeIdAndPage)),
    stateful()
  );

  readonly currentDocument$ = this.pathInfo$.pipe(
    map((i) => i?.document),
    stateful((a, b) => a?.id === b?.id)
  );

  readonly selectedPath$ = this.pathInfo$.pipe(
    map((i) => i?.path),
    stateful()
  );

  readonly orgsAndDocTypes$ = this.cacheService.getOrgsAndDocTypes();

  readonly cantonsNode$ = this.cacheService.getNavigationNode(CANTONS_NODEID);
  readonly communesNode$ = this.cacheService.getNavigationNode(COMMUNES_NODEID);

  readonly currentPublication$ = this.pathInfo$.pipe(
    map((i) => i?.publication),
    stateful((a, b) => a?.id === b?.id)
  );

  readonly currentOrganizationId$ = this.pathInfo$.pipe(
    map((i) => i?.organization?.id as OrganizationId | undefined),
    stateful()
  );

  readonly resetNavExpansion$ = new BehaviorSubject(false);

  isExpanded(node: ExtendedHierarchyNode) {
    return this.selectedPath$.pipe(
      map((path) => NodeVM.isOrganization(node) || path?.includes(node.id)),
      distinctUntilChanged()
    );
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getNodePath(nodeId: NodeId | undefined): Observable<HierarchyNodeViewModel[] | null> {
    return !nodeId ? of(null) : this.nodeService.getPathFromRoot(nodeId, "root", false);
  }

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  getPathInfo(nodeIdAndPage: NodeIdAndPage | undefined): Observable<PathInfo | null> {
    if (nodeIdAndPage == null) return of(null);
    const { nodeId, pageNumber } = nodeIdAndPage;
    return this.getNodePath(nodeId).pipe(
      map((pathNodes) => {
        if (!pathNodes) return pathNodes;
        const path = pathNodes.map((n) => n.id);
        const document = pathNodes?.find(NodeVM.isDocument);
        const content = document ? closestContent(pathNodes) : nodeToContent(pathNodes[pathNodes.length - 1]);

        const organization = pathNodes?.find(NodeVM.isOrganization);
        const chapter = pathNodes?.find(NodeVM.isChapter);
        const publication = pathNodes?.find(NodeVM.isPublication);

        const page = pageNumber ?? chapter?.startPage ?? document?.startPage;
        const info: PathInfo = { nodeId, path, organization, publication, document, page, contentNode: content, chapter };
        return info;
      })
    );
  }

  resetNavigationTree() {
    this._forcedNodeId$.next(undefined);
    this.resetNavExpansion$.next(true);
    this.resetNavExpansion$.next(false);
  }
}

// Helper function

function stateful<T>(compare?: (x: T, y: T) => boolean): MonoTypeOperatorFunction<T> {
  return function (source: Observable<T>): Observable<T> {
    return source.pipe(distinctUntilChanged(compare), shareReplay({ refCount: true, bufferSize: 1 }));
  };
}

function nodeToContent(node: HierarchyNodeViewModel | undefined): ContentNode | undefined {
  return node && { content: node.content, titles: [node.title].filter(isTruthy) };
}

function closestContent(path: HierarchyNodeViewModel[]): ContentNode {
  const reversePath = path.filter((node) => NodeVM.isDocument(node) || NodeVM.isChapter(node)).reverse();
  const content = reversePath.find((node) => node.content)?.content;
  const titles: string[] = [];
  for (const node of path) {
    if (node.title) titles.unshift(node.title);
    if (NodeVM.isPublication(node)) break;
  }
  return { content, titles };
}
